// Shared helpers for the admin Stock Control area. Keeps the page and its
// components working from one definition of "tracked", "low" and "allocated".

// Standard-rate VAT, used to strip VAT off the retail price for margin maths.
export const VAT_RATE = 0.2;

export const FILTERS = [
  { key: 'all', label: 'All Items' },
  { key: 'low', label: 'Low Stock' },
  { key: 'out', label: 'Out of Stock' },
  { key: 'made_to_order', label: 'Made-to-Order' },
];

export const ADJUSTMENT_REASONS = [
  { value: 'stocktake', label: 'Stocktake' },
  { value: 'restock', label: 'Restock / Delivery' },
  { value: 'stock_received', label: 'Stock Received' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'shrinkage', label: 'Shrinkage / Loss' },
  { value: 'returned', label: 'Customer Return' },
  { value: 'correction', label: 'Correction' },
  { value: 'other', label: 'Other' },
];

// Workshop / built-to-order lines are built on demand, so their stock never
// comes from (or goes back to) Square.
export function isMadeToOrder(product) {
  return !!product.manual_product || product.lead_time_days != null;
}

// tracked       = Square holds a live inventory figure for this variation
// made_to_order = built to order in the workshop
// untracked     = sold freely, Square holds no stock figure
export function stockStatus(product, level) {
  if (isMadeToOrder(product)) return 'made_to_order';
  if (!product.square_variation_id || !level) return 'untracked';
  return 'tracked';
}

// Quantity committed to trade orders that Square has not been adjusted for yet.
// An invoiced order has already left Square, so counting it again would double
// up the commitment. Keyed by catalogue product id.
export function buildAllocationMap(orders, products) {
  const byId = new Map((products || []).map((p) => [p.id, p]));
  const bySku = new Map(
    (products || []).filter((p) => p.sku).map((p) => [String(p.sku).trim().toLowerCase(), p]),
  );
  const allocated = new Map();
  for (const order of orders || []) {
    if (order.status === 'cancelled' || order.square_stock_deducted) continue;
    for (const li of order.line_items || []) {
      if (!li.stock_allocated) continue;
      const product =
        (li.product_id && byId.get(li.product_id)) ||
        bySku.get(String(li.product_code || '').trim().toLowerCase());
      if (!product) continue;
      allocated.set(product.id, (allocated.get(product.id) || 0) + (Number(li.qty) || 0));
    }
  }
  return allocated;
}

const SEVERITY = { out: 0, low: 1, tracked: 2, workshop: 3 };

// One row per catalogue product/variation with everything the table renders.
export function buildStockRows(products, levels, allocated) {
  const rows = (products || []).map((product) => {
    const level = product.square_variation_id ? levels?.[product.square_variation_id] : null;
    const status = stockStatus(product, level);
    const onHand = status === 'tracked' ? level.quantity : null;
    const committed = allocated?.get(product.id) || 0;
    const threshold = product.reorder_point != null ? Number(product.reorder_point) : null;
    const isOut = status === 'tracked' && onHand <= 0;
    const isLow = status === 'tracked' && !isOut && threshold != null && onHand <= threshold;

    // Margin is worked out on the ex-VAT selling price, so it compares like for
    // like against the ex-VAT cost price.
    const cost = product.cost_price_ex_vat != null ? Number(product.cost_price_ex_vat) : null;
    const exVat = product.price != null ? Number(product.price) / (1 + VAT_RATE) : null;
    const margin = cost != null && exVat != null ? exVat - cost : null;
    const marginPct = margin != null && exVat > 0 ? (margin / exVat) * 100 : null;

    return {
      product,
      level,
      status,
      onHand,
      committed,
      available: onHand == null ? null : onHand - committed,
      threshold,
      isOut,
      isLow,
      cost,
      exVat,
      margin,
      marginPct,
    };
  });

  const severityOf = (r) => (r.isOut ? SEVERITY.out : r.isLow ? SEVERITY.low
    : r.status === 'tracked' ? SEVERITY.tracked : SEVERITY.workshop);

  return rows.sort((a, b) => {
    const diff = severityOf(a) - severityOf(b);
    if (diff !== 0) return diff;
    return String(a.product.name || '').localeCompare(String(b.product.name || ''));
  });
}

export function matchesFilter(row, filter) {
  if (filter === 'low') return row.isLow || row.isOut;
  if (filter === 'out') return row.isOut;
  if (filter === 'made_to_order') return row.status === 'made_to_order';
  return true;
}

export function matchesSearch(row, search) {
  if (!search) return true;
  const q = search.toLowerCase();
  return [
    row.product.name,
    row.product.variation_name,
    row.product.sku,
    row.product.option_value,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(q));
}

// Lines are grouped by the app's own category, falling back to the Square
// reporting category for anything that has no storefront category yet. The 'rc:'
// prefix keeps the two kinds of key apart so a category can't be double-counted.
export function categoryKeyOf(product) {
  if (product.category) return product.category;
  if (product.reporting_category) return `rc:${product.reporting_category}`;
  return '';
}

export function matchesCategory(row, key) {
  if (!key) return true;
  return categoryKeyOf(row.product) === key;
}